import { z } from "zod";
import { AffiliateNetwork, AffiliateNetworkStatus, ConversionMapping, PayoutModel, PostbackSecurity } from "@gen/tracker/v1/affiliate_network_pb";
import { ConnectError } from "@connectrpc/connect";
import { FieldError as FieldErrorMessage, type FieldError } from "@gen/tracker/v1/common_pb";
import { fieldFromError } from "@/lib/connect";

/**
 * The partner editor's form (board PartnerEditor): name, payout currency and
 * default model, the partner's cabinet; who may call; how long a silence
 * may last. What the postback calls the click, the payout and the lead id is
 * the Edge's built-in aliases (owner, 21.10), the status params stay the
 * mapping's own. The status
 * table itself is the card's (PartnerCard «Как читать статусы»). Pure.
 */

export const CURRENCIES = ["USD", "EUR", "RUB", "GBP", "UAH", "KZT", "PLN", "BRL"] as const;

/** «Сообщить, если тишина дольше»: the choices, in seconds; 0 = never. */
export const SILENCE_CHOICES = [0, 6 * 3600, 12 * 3600, 24 * 3600, 48 * 3600, 72 * 3600, 7 * 86400] as const;

export const partnerFormSchema = (t: (key: "u6.err.nameRequired") => string) =>
  z.object({
    name: z.string().trim().min(1, t("u6.err.nameRequired")).max(120),
    currency: z.string().length(3),
    defaultModel: z.string(),
    cabinetUrl: z.string(),
    ipAllowlist: z.array(z.string()),
    silence: z.string(),
  });

export type PartnerFormValues = z.infer<ReturnType<typeof partnerFormSchema>>;

export function partnerToForm(n: AffiliateNetwork | undefined): PartnerFormValues {
  return {
    name: n?.name ?? "",
    currency: n?.currency || "USD",
    defaultModel: String(n?.defaultPayoutModel || PayoutModel.CPA),
    cabinetUrl: n?.cabinetUrl ?? "",
    ipAllowlist: n ? [...(n.postbackSecurity?.ipAllowlist ?? [])] : [],
    silence: String(n?.silenceAlertSeconds ?? 24 * 3600),
  };
}

/**
 * The partner to send. `base` is the partner as read: its mapping words,
 * token, attribution policy and notes ride along unchanged — the token is
 * immutable server-side anyway (a new one would break every cabinet).
 */
export function formToPartner(v: PartnerFormValues, base: AffiliateNetwork | undefined): AffiliateNetwork {
  const n = base ? base.clone() : new AffiliateNetwork({ status: AffiliateNetworkStatus.ACTIVE });
  n.name = v.name.trim();
  n.currency = v.currency;
  n.defaultPayoutModel = Number(v.defaultModel) as PayoutModel;
  n.cabinetUrl = v.cabinetUrl.trim();
  n.silenceAlertSeconds = BigInt(Number(v.silence) || 0);
  n.postbackSecurity = new PostbackSecurity({ ...(base?.postbackSecurity ?? {}), ipAllowlist: v.ipAllowlist });
  n.conversionMapping = base?.conversionMapping?.clone() ?? new ConversionMapping();
  return n;
}

/** Where a server problem on the partner belongs: the form field, or "root". Pure. */
export function partnerField(e: Pick<FieldError, "path">): keyof PartnerFormValues | "root" {
  const head = e.path.replace(/\[.*$/, "");
  switch (head) {
    case "name":
      return "name";
    case "currency":
      return "currency";
    case "cabinet_url":
      return "cabinetUrl";
    case "silence_alert_seconds":
      return "silence";
    default:
      return "root";
  }
}

/**
 * Every problem a refused write carries — CreateAffiliateNetwork and
 * UpdateAffiliateNetwork answer all of them as tracker.v1.FieldError details,
 * the first also in X-Field. Pure.
 */
export function fieldErrorsOf(err: unknown): { path: string; message: string }[] {
  const ce = ConnectError.from(err);
  const details = ce.findDetails(FieldErrorMessage).map((d) => ({ path: d.path, message: d.message }));
  if (details.length > 0) return details;
  const field = fieldFromError(err);
  return field ? [{ path: field, message: ce.rawMessage }] : [];
}
